import React, { Profiler, type RefObject, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { type GasPayload } from '@atlaskit/analytics-gas-types';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { localStore as knowledgeDiscoveryLocalStore } from '@atlassian/search-ai';
import {
	type ActivityItemEdge,
	isGraphActivity,
	isSearchConfluenceSpace,
	isSearchResultGraphEntity,
	isSearchResultJiraIssue,
} from '@atlassian/search-client';

import { ApplicationModes } from '../../../common/constants/application-modes';
import { type PrimaryProductKey, PrimaryProductKeys } from '../../../common/constants/products';
import {
	PRODUCTS_WITH_INLINE_FILTERS,
	QUICK_FIND_LOADING_HOLD,
	SEARCH_DIALOG_WIDTH_OBSERVER_ID,
} from '../../../common/constants/quick-find';
import { useAppContext } from '../../../common/ui/app-context';
import { BulkConnectBanner } from '../../../common/ui/bulk-connect-banner';
import { BrowserHistoryHeading } from '../../../common/ui/quick-find/browser-history-heading';
import { Heading } from '../../../common/ui/quick-find/heading';
import { ItemsAnalyticsProvider } from '../../../common/ui/quick-find/items-analytics-provider';
import {
	isAutocompleteSuggestionItem,
	isBrowserHistoryHeadingItem,
	isBrowserHistoryItem,
	isErrorItem,
	isFilterSuggestionItem,
	isHeadingItem,
	isLandingItem,
	isNoResultsItem,
	isPeopleAndTeamsItem,
	isRecentActivityItem,
	isRecentQueryItem,
	isSearchResultItem,
	isSkeletonItem,
	isUserDocumentItem,
	isZeroQueryItem,
	type Item,
} from '../../../common/ui/quick-find/types';
import { useWidthObserverRegistration } from '../../../common/ui/width-observer';
import { OAuthSource } from '../../../common/utils/events/oauth-events';
import { serveGithubLinksHydrationWithoutExposure } from '../../../common/utils/experiment';
import {
	serve3PBulkConnectWithoutExposure,
	serveQFFilterSuggestionExperiment,
} from '../../../common/utils/experiments';
import { mergeRefs } from '../../../common/utils/merge-refs';
import { OAuthContainer } from '../../../common/utils/oauth-container';
import { onSearchDialogDismissed } from '../../../common/utils/quick-find/events/search-dialog-dismissed';
import { onSearchDialogShown } from '../../../common/utils/quick-find/events/search-dialog-shown';
import { onSearchResultsRendered } from '../../../common/utils/quick-find/events/search-results-rendered';
import {
	isLoadingState,
	isNotResultsState,
	isPostQueryLoadingOrResults,
	isPostQueryState,
	isPreQueryState,
	State,
} from '../../../common/utils/quick-find/state';
import { isOnJiraFullPageSearch } from '../../../common/utils/url';
import ShowIfEligible from '../../../controllers/application-modes';
import { useNewlyConnectedProducts } from '../../../controllers/bulk-connect';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useOAuthHandlers } from '../../../controllers/oauth';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../controllers/quick-find/analytics';
import type {
	QuickFindContent,
	QuickFindResult,
} from '../../../controllers/quick-find/fetch-results/types';
import { useQuickFindPostQueryVitalStats } from '../../../controllers/quick-find/use-quickfind-post-query-vitalstats';
import { useResultsShownCallbacks } from '../../../controllers/quick-find/use-results-shown-callback';
import {
	useQuickFindFooterOnClick,
	useQuickFindResultOnClick,
	useQuickFindResultOnContextMenu,
	useQuickFindResultOnHighlighted,
} from '../../../controllers/quick-find/utils';
import {
	useBootstrap,
	useIsHello,
	usePrimaryProduct,
	useSearchActions,
} from '../../../controllers/store';
import {
	useBootstrapActions,
	useUserShardModel,
	useUserShardUI,
} from '../../../controllers/store/bootstrap';
import { useContent, useIsBulkConnectModalOpen } from '../../../controllers/store/quick-find';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { BulkConnectModal } from '../../search-page/bulk-connect-modal';

import { AutocompleteSuggestion } from './autocomplete-suggestion';
import { AssetsUnavailableBanner } from './banners/assets';
import { RovoTipBanner } from './banners/rovo';
import { BrowserHistory } from './browser-history';
import { Divider } from './divider';
import { ErrorScreen } from './error-screen';
import { FeedbackFooter } from './feedback-footer';
import { FilterSuggestion } from './filter-suggestion';
import { QuickFindFilters } from './filters';
import { JiraAdvancedSearchLink } from './jira/jira-advanced-search-link';
import { JiraSingleSiteSearch } from './jira/jira-single-site-search';
import { LandingScreen } from './landing-screen';
import { messages } from './messages';
import { NoResultsScreen } from './no-results-screen';
import { PeopleAndTeams } from './people-and-teams';
import { ProductEntity } from './product-entity';
import { useQueryFilterResults } from './query-filters';
import { RecentActivity } from './recent-activity';
import { RecentQuery } from './recent-query';
import { SearchResultSkeleton } from './result-skeleton';
import { SearchPageLink } from './search-page-link';
import { UserDocument } from './user-document';
import { ZeroQuery } from './zero-query';

const searchDialogStyles = xcss({
	display: 'flex',
	position: 'absolute',
	flexDirection: 'column',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	width: '100%',
});

const searchDialogDefaultStyles = xcss({
	borderRadius: token('radius.small'),
	marginTop: 'space.100',
});

const searchDialogRefreshStyles = xcss({
	borderRadius: token('radius.large'),
	marginTop: 'space.050',
});

const searchResultsSectionStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	paddingBlockStart: 'space.100',
	paddingBlockEnd: 'space.100',
	gap: 'space.025',
	overflowY: 'auto',
});

const footerStyles = xcss({
	flexGrow: 1,
});

const footerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	[media.above.lg]: {
		flexDirection: 'row',
	},
});

const footerSpotlightTargetContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-end',
	marginInlineEnd: 'space.400',
	[media.above.lg]: {
		marginInlineEnd: 'unset',
		paddingBlockStart: 'space.1000',
	},
});

const footerSpotlightTargetStyles = xcss({
	marginBlockStart: 'space.negative.400',
	[media.above.lg]: {
		marginBlockStart: 'unset',
	},
});

const defaultSearchResultsSectionHeightStyles = xcss({
	// 86px for the QF footer + 48px for the page header + 86px bottom gap
	maxHeight: 'calc(100vh - 220px)',
});

const jiraSearchResultsSectionHeightStyles = xcss({
	// 167px for QF footer + 48px for the page header + 15% gap at the bottom
	maxHeight: 'calc(85vh - 215px)',
});

const SearchResultsSectionWrapper = ({
	children,
	primaryProduct,
}: {
	children: React.ReactNode;
	primaryProduct: PrimaryProductKey | undefined;
}) => {
	const useDefaultHeight = !(
		primaryProduct === PrimaryProductKeys.Jira && fg('search_qf_increase_issues_in_jira')
	);

	return (
		<Box
			xcss={[
				searchResultsSectionStyles,
				useDefaultHeight
					? defaultSearchResultsSectionHeightStyles
					: jiraSearchResultsSectionHeightStyles,
			]}
		>
			{children}
		</Box>
	);
};

type SearchDialogProps = {
	dialogId: string;
	dialogRef: RefObject<HTMLDivElement>;
	inputRef?: RefObject<HTMLInputElement>;
	localQuery: string;
	setLocalQuery: React.Dispatch<React.SetStateAction<string>>;
	PlacementPO?: () => JSX.Element;
	showFilters?: boolean;
	searchRefreshStyling?: boolean;
};
export const SearchDialog = ({
	dialogId,
	dialogRef,
	localQuery,
	setLocalQuery,
	inputRef,
	PlacementPO,
	showFilters = true,
	searchRefreshStyling = false,
}: SearchDialogProps): React.JSX.Element | null => {
	const intl = useIntl();

	const [primaryProduct] = usePrimaryProduct();
	const { retrySearch } = useSearchActions();

	const [{ thirdPartyConfigs, products: availableProducts, onHistoryHeadingClick }] =
		useBootstrap();
	const serveUserShardModel = useUserShardModel();
	const serveUserShardUI = useUserShardUI();
	const content = useContent();
	const [isHello] = useIsHello();

	const serveZeroQuery =
		((primaryProduct === PrimaryProductKeys.Confluence ||
			primaryProduct === PrimaryProductKeys.Atlas ||
			(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))) &&
			fg('kd_zero_query_in_qf_fg')) ||
		primaryProduct === PrimaryProductKeys.Jira;

	const allItems = useBody(content, serveUserShardUI, serveZeroQuery, primaryProduct);

	useResetSessionAnalytics();
	const { fireAnalyticsEvent, fireAnalyticsScreenEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	useAnalyticsEvents(fireAnalyticsEvent, commonAttributes, nonPrivacySafeAttributes);

	const resultOnClick = useQuickFindResultOnClick();
	const resultOnContextMenu = useQuickFindResultOnContextMenu();
	const resultOnHighlighted = useQuickFindResultOnHighlighted();
	const footerOnClick = useQuickFindFooterOnClick();

	const queryFiltersBody = useQueryFilterResults({ localQuery, setLocalQuery, inputRef });

	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	let hasThirdPartyResults = false;

	const localDialogRef = useRef(null);
	useWidthObserverRegistration(SEARCH_DIALOG_WIDTH_OBSERVER_ID, localDialogRef);

	const { getNewlyConnectedProducts } = useNewlyConnectedProducts();

	useEffect(() => {
		// The allocation of the cohort is done in src/controllers/quick-find/fetch-results/activities/index.tsx.
		// This is a manual trigger for the exposure event after a user interacts (opens) with the dialog.
		FeatureGates.getExperimentValue<string>(
			'remove-jira-recents-from-quick-find-in-confluence',
			'cohort',
			'control',
			{ fireExperimentExposure: true },
		);
	}, []);

	const serveGithubLinksHydration = serveGithubLinksHydrationWithoutExposure();

	if (!content) {
		return null;
	}

	const swapQuickFindCTAs =
		isOnJiraFullPageSearch() &&
		primaryProduct &&
		primaryProduct === PrimaryProductKeys.Jira &&
		fg('jira_search_fps_cta_swap');

	const { state } = content;

	const body = [];
	let searchResultItemBody: React.JSX.Element[] | undefined;

	for (const content of allItems) {
		if (isErrorItem(content)) {
			body.push(<ErrorScreen key={content.id} onRetry={retrySearch} />);
		}

		if (isLandingItem(content)) {
			body.push(<LandingScreen key={content.id} />);
		}

		if (isNoResultsItem(content)) {
			body.push(<NoResultsScreen key={content.id} onRetry={retrySearch} />);
		}

		if (isHeadingItem(content)) {
			body.push(
				// eslint-disable-next-line @atlassian/a11y/heading-has-content -- See https://go/a11y-heading-has-content for more details
				<Heading
					key={content.id}
					id={content.id}
					text={content.data}
					includeInAnalytics={content.includeInAnalytics}
				/>,
			);
		}

		if (isBrowserHistoryHeadingItem(content)) {
			const headingToUse = onHistoryHeadingClick ? (
				<BrowserHistoryHeading
					key={content.id}
					id={content.id}
					leftText={content.leftText}
					rightText={content.rightText}
					onRightTextClick={onHistoryHeadingClick}
					fireAnalyticsEvent={fireAnalyticsEvent}
					commonAttributes={commonAttributes}
					nonPrivacySafeAttributes={nonPrivacySafeAttributes}
				/>
			) : (
				// eslint-disable-next-line @atlassian/a11y/heading-has-content -- See https://go/a11y-heading-has-content for more details
				<Heading
					key={content.id}
					id={content.id}
					text={content.leftText}
					includeInAnalytics={true}
				/>
			);

			body.push(headingToUse);
		}

		if (isSkeletonItem(content)) {
			body.push(<SearchResultSkeleton key={content.id} />);
		}

		if (isRecentQueryItem(content)) {
			body.push(<RecentQuery key={content.id} result={content.data} state={state} />);
		}

		if (isAutocompleteSuggestionItem(content)) {
			body.push(<AutocompleteSuggestion key={content.id} result={content.data} state={state} />);
		}

		if (isRecentActivityItem(content)) {
			const activity = (
				<RecentActivity
					key={content.id}
					result={content.data}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
					serveGithubLinksHydration={serveGithubLinksHydration}
				/>
			);
			if (serveUserShardUI) {
				if (!searchResultItemBody) {
					searchResultItemBody = [];
					body.push(
						<Profiler
							id="QuickFindSearchDialogResults"
							onRender={(_, phase, actualDuration) => {
								if (phase === 'mount' && localQuery.length) {
									fireAnalyticsEvent(
										onSearchResultsRendered({
											attributes: {
												...commonAttributes,
												durationMs: actualDuration,
												hasThirdPartyResults,
											},
											nonPrivacySafeAttributes,
										}),
									);
								}
							}}
						>
							{searchResultItemBody}
						</Profiler>,
					);
				}
				searchResultItemBody.push(activity);
			} else {
				body.push(activity);
			}
		}

		if (isBrowserHistoryItem(content)) {
			body.push(
				<BrowserHistory
					key={content.id}
					result={content}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
				/>,
			);
		}

		if (isUserDocumentItem(content)) {
			body.push(
				<UserDocument
					key={content.id}
					result={content.data}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
				/>,
			);
		}

		if (isZeroQueryItem(content) && serveZeroQuery) {
			body.push(
				<ZeroQuery
					key={content.id}
					result={content.data}
					product={content.product}
					avatarUrl={content.avatarUrl}
					state={state}
					zeroQueryType={content.zeroQueryType}
					zeroQueryPosition={allItems.indexOf(content)}
					// accountId={content.accountId}
					// dateRange={content.dateRange}
				/>,
			);
		}

		if (isFilterSuggestionItem(content)) {
			body.push(<FilterSuggestion key={content.id} result={content.data} state={state} />);
		}

		if (isSearchResultItem(content)) {
			const productEntity = (
				<ProductEntity
					key={content.id}
					result={content.data}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
				/>
			);

			if (serveUserShardModel) {
				if (!searchResultItemBody) {
					searchResultItemBody = [];
					body.push(
						<Profiler
							id="QuickFindSearchDialogResults"
							onRender={(_, phase, actualDuration) => {
								if (phase === 'mount' && localQuery.length) {
									fireAnalyticsEvent(
										onSearchResultsRendered({
											attributes: {
												...commonAttributes,
												durationMs: actualDuration,
												hasThirdPartyResults,
											},
											nonPrivacySafeAttributes,
										}),
									);
								}
							}}
						>
							{searchResultItemBody}
						</Profiler>,
					);
				}

				searchResultItemBody.push(productEntity);
				if (isSearchResultGraphEntity(content.data)) {
					hasThirdPartyResults = true;
				}
			} else {
				body.push(productEntity);
			}
		}

		if (isPeopleAndTeamsItem(content)) {
			body.push(
				<PeopleAndTeams
					key={content.id}
					result={content.data}
					state={state}
					onClick={resultOnClick}
					onContextMenu={resultOnContextMenu}
					onHighlighted={resultOnHighlighted}
				/>,
			);
		}
	}

	const footer = (() => {
		// Post-query state with Confluence, Focus, or RovoExtension
		if (
			(primaryProduct === PrimaryProductKeys.Confluence ||
				primaryProduct === PrimaryProductKeys.Focus ||
				(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))) &&
			isPostQueryState(state)
		) {
			return (
				<>
					<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
						<Divider />
						<SearchPageLink state={state} onClick={footerOnClick} />
					</ShowIfEligible>
					<Divider />
					<FeedbackFooter />
				</>
			);
		}

		if (primaryProduct === PrimaryProductKeys.Jira) {
			return (
				<>
					<JiraSingleSiteSearch state={state} onClick={footerOnClick} />
					{swapQuickFindCTAs ? (
						<>
							<SearchPageLink state={state} onClick={footerOnClick} />
							<JiraAdvancedSearchLink state={state} onClick={footerOnClick} />
						</>
					) : (
						<>
							<JiraAdvancedSearchLink state={state} onClick={footerOnClick} />
							<SearchPageLink state={state} onClick={footerOnClick} />
						</>
					)}
					<Divider />
					<FeedbackFooter />
				</>
			);
		}

		// Pre-query footer
		return (
			<>
				<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
					<Divider />
					<SearchPageLink state={state} onClick={footerOnClick} />
				</ShowIfEligible>
				<Divider />
				<FeedbackFooter />
			</>
		);
	})();

	const isAIEnabled = isRovoLLMEnabled || isAdminHubAIEnabled;
	const displayFilters = PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct);
	const nonResultsBody = isNotResultsState(state);

	return (
		<Box
			xcss={[
				searchDialogStyles,
				searchRefreshStyling ? searchDialogRefreshStyles : searchDialogDefaultStyles,
			]}
			aria-label={intl.formatMessage(messages.searchDialogAriaLabel)}
			testId={'search-dialog'}
			ref={mergeRefs(dialogRef, localDialogRef)}
			id={dialogId}
		>
			<ItemsAnalyticsProvider allItems={allItems}>
				{queryFiltersBody ? (
					<SearchResultsSectionWrapper primaryProduct={primaryProduct}>
						{queryFiltersBody}
					</SearchResultsSectionWrapper>
				) : (
					<>
						<SearchResultsSectionWrapper primaryProduct={primaryProduct}>
							{primaryProduct === PrimaryProductKeys.Assets && !fg('assets_rovo_search_all') && (
								<AssetsUnavailableBanner />
							)}
							<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
								{isPreQueryState(state) &&
									(serve3PBulkConnectWithoutExposure() ? (
										<ErrorBoundary section="QuickFindSearchDialogBulkConnectBanner">
											<>
												<BulkConnectBanner
													thirdPartyConfigs={thirdPartyConfigs}
													availableProducts={availableProducts}
													getNewlyConnectedProducts={getNewlyConnectedProducts}
													useIsBulkConnectModalOpenQuickFind={useIsBulkConnectModalOpen}
													isOnSearchDialog
													fireAnalyticsScreenEvent={fireAnalyticsScreenEvent}
													fireAnalyticsEvent={fireAnalyticsEvent}
													commonAttributes={commonAttributes}
												/>
												<BulkConnectModal
													isOnSearchDialog
													oAuthSource={OAuthSource.quickFindBulkConnect}
												/>
											</>
										</ErrorBoundary>
									) : (
										<ErrorBoundary section="QuickFindSearchDialogOAuthContainer">
											<OAuthContainer
												isOnSearchDialog
												thirdPartyConfigs={thirdPartyConfigs}
												availableProducts={availableProducts}
												isHello={isHello}
												useOAuthHandlers={useOAuthHandlers}
												fireAnalyticsEvent={fireAnalyticsEvent}
											/>
										</ErrorBoundary>
									))}
								{showRovoTipBanner(state, isAIEnabled) && !serveZeroQuery && <RovoTipBanner />}
								{PlacementPO && <PlacementPO />}
								{(displayFilters || isPostQueryState(state)) && showFilters && (
									<Flex direction="column">
										<QuickFindFilters />
										<Box paddingInlineStart="space.400">
											<Box id="spotlight-multi-step-on-trigger-step-1" />
										</Box>
									</Flex>
								)}
							</ShowIfEligible>
							{isLoadingState(state) && <UFOLoadHold name={QUICK_FIND_LOADING_HOLD} />}
							<Stack {...(!nonResultsBody ? { role: 'list' } : {})} space="space.025">
								{body}
							</Stack>
						</SearchResultsSectionWrapper>
						<Box xcss={footerContainerStyles}>
							<Box xcss={footerStyles}>{footer}</Box>
							<Box xcss={footerSpotlightTargetContainerStyles}>
								<Box
									xcss={footerSpotlightTargetStyles}
									id="spotlight-multi-step-on-trigger-step-2"
								/>
							</Box>
						</Box>
					</>
				)}
			</ItemsAnalyticsProvider>
		</Box>
	);
};

const setRecentActivityCount = (filteredActivities: any[]) => {
	if (fg('kd_recent_activity_intent')) {
		knowledgeDiscoveryLocalStore.setString(
			'recent-activity-count',
			filteredActivities.length.toString(),
		);
	}
};

const getJiraSuggestionsAndRecentQueries = ({
	recentQueries: inputRecentQueries,
	zeroQueries: inputZeroQueries,
}: {
	recentQueries: QuickFindResult<string>[];
	zeroQueries: QuickFindResult<string>[];
}) => {
	const suggestionsAndRecentQueries: QuickFindResult<string>[] = [];
	const zeroQueries = inputZeroQueries.slice(0, 1);
	const dedupedRecentQueries = inputRecentQueries
		.filter((recentQuery) => !zeroQueries.map((q) => q.data).includes(recentQuery.data))
		.slice(0, 1);

	if (zeroQueries.length > 0 && dedupedRecentQueries.length > 0) {
		suggestionsAndRecentQueries.push(...zeroQueries);
		suggestionsAndRecentQueries.push(...dedupedRecentQueries);
	} else if (zeroQueries.length > 0) {
		// show up to 2 zero queries if no deduped recent queries
		suggestionsAndRecentQueries.push(...inputZeroQueries.slice(0, 2));
	} else if (dedupedRecentQueries.length > 0) {
		// show up to 2 recent queries if no zero queries
		suggestionsAndRecentQueries.push(...inputRecentQueries.slice(0, 2));
	}
	return suggestionsAndRecentQueries;
};

export const useBody = (
	content: QuickFindContent,
	serveUserShardUI: boolean,
	serveZeroQuery: boolean,
	primaryProduct?: PrimaryProductKey,
) => {
	const intl = useIntl();

	const { onSearchResultsShown, handleRecentActivitiesCallback } = useResultsShownCallbacks();
	const serveFilterSuggestion = serveQFFilterSuggestionExperiment(primaryProduct, {
		fireExperimentExposure: false,
	});

	const userRelevantDocumentsEnabled = useMemo(() => {
		return (
			!serveUserShardUI &&
			primaryProduct === PrimaryProductKeys.Confluence &&
			fg('quick_find_user_documents')
		);
	}, [primaryProduct, serveUserShardUI]);

	return useMemo(() => {
		if (!content) {
			return [];
		}

		const { state } = content;

		const browserHistoryActivities =
			'browserHistoryActivities' in content ? content.browserHistoryActivities : undefined;
		const shouldUseBrowserHistory = !!browserHistoryActivities?.length;

		const items: Item[] = [];

		const recentsHeadingItem = {
			id: 'recent-activities',
			type: 'heading' as const,
			data: intl.formatMessage(messages.recentsHeading),
		};

		const recommendationsHeadingItem = {
			id: 'recent-activities',
			type: 'heading' as const,
			data: intl.formatMessage(messages.recommendationsHeading),
		};

		const headingItem = userRelevantDocumentsEnabled
			? recommendationsHeadingItem
			: recentsHeadingItem;

		const browserHistoryHeadingItem = {
			id: 'browser-history',
			type: 'browser-history-heading' as const,
			leftText: intl.formatMessage(messages.browserHistoryHeading),
			rightText: intl.formatMessage(messages.browserHistoryHeadingLearnMore),
		};

		const searchResultsHeadingItem = {
			id: 'search-results',
			type: 'heading' as const,
			data: intl.formatMessage(
				serveUserShardUI ? messages.userShardHeading : messages.searchResultsHeading,
			),
		};

		let filteredRecentActivities: QuickFindResult<ActivityItemEdge>[] = [];

		if (state === State.PREQUERY_ERROR) {
			items.push({ id: 'error', type: 'error' });
		}

		if (state === State.PREQUERY_LOADING && !shouldUseBrowserHistory) {
			items.push(...content.recentQueries.slice(0, 3));
			items.push(headingItem);
			for (let i = 0; i < 7; i++) {
				items.push({ id: `skeleton-${i}`, type: 'skeleton' });
			}
		}

		if (state === State.PREQUERY_NO_RESULTS) {
			items.push({ id: 'landing', type: 'landing' });
		}

		if (state === State.PREQUERY_RESULTS) {
			let zeroQueriesAndRecentQueriesCount = 3;
			let zeroQueriesCount = 2;
			if (serveFilterSuggestion && content.filterSuggestions.length > 0) {
				zeroQueriesAndRecentQueriesCount = 2;
				// filter suggestion replaces one of the zero queries
				zeroQueriesCount = 1;
				items.push(...content.filterSuggestions);
			}

			if (serveZeroQuery) {
				const visibleZeroQueries = content.zeroQueries.slice(0, zeroQueriesCount);
				const dedupedVisibleRecentQueries = content.recentQueries
					.filter((query) => !visibleZeroQueries.some((zeroQuery) => zeroQuery.data === query.data))
					.slice(0, zeroQueriesAndRecentQueriesCount - visibleZeroQueries.length);
				items.push(...visibleZeroQueries);
				items.push(...dedupedVisibleRecentQueries);
			} else {
				items.push(...content.recentQueries.slice(0, zeroQueriesAndRecentQueriesCount));
			}

			if (shouldUseBrowserHistory) {
				items.push(browserHistoryHeadingItem);
				const browserHistoryItems = browserHistoryActivities.slice(0, 7).map((item) => ({
					id: item.id,
					type: 'browser-history',
					data: item.data,
				}));

				items.push(...browserHistoryItems);
			} else if (userRelevantDocumentsEnabled) {
				if (content.userDocuments.length + content.recentActivities.length > 0) {
					items.push(recommendationsHeadingItem);
					const filteredUserDocuments = content.userDocuments.slice(0, 4);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.filter((recentlyViewedResult) => {
							const isDuplicate = filteredUserDocuments.some((userDocument) => {
								const activityObject = recentlyViewedResult.data.node.object;
								const id = isGraphActivity(activityObject)
									? activityObject.data?.id
									: recentlyViewedResult.id;
								return id === userDocument.id;
							});
							return !isDuplicate;
						})
						.slice(0, 7 - filteredUserDocuments.length);
					items.push(...filteredUserDocuments);
					items.push(...filteredRecentActivities);
				}
			} else {
				if (content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.slice(0, 7);
					items.push(...filteredRecentActivities);
				}
			}
			setRecentActivityCount(filteredRecentActivities);
		}

		if (state === State.POSTQUERY_ERROR) {
			items.push({ id: 'error', type: 'error' });
		}

		if (state === State.POSTQUERY_LOADING) {
			if (primaryProduct === PrimaryProductKeys.Jira) {
				items.push(
					...getJiraSuggestionsAndRecentQueries({
						recentQueries: content.recentQueries,
						zeroQueries: content.zeroQueries,
					}),
				);
			}

			if (shouldUseBrowserHistory) {
				items.push(browserHistoryHeadingItem);
				const browserHistoryItemsLoading = browserHistoryActivities.slice(0, 5).map((item) => ({
					id: item.id,
					type: 'browser-history',
					data: item.data,
				}));
				items.push(...browserHistoryItemsLoading);
			} else if (userRelevantDocumentsEnabled) {
				if (content.userDocuments.length + content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					const filteredUserDocuments = content.userDocuments.slice(0, 5);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.filter((recentlyViewedResult) => {
							const isDuplicate = filteredUserDocuments.some((userDocument) => {
								const activityObject = recentlyViewedResult.data.node.object;
								const id = isGraphActivity(activityObject)
									? activityObject.data?.id
									: recentlyViewedResult.id;
								return id === userDocument.id;
							});
							return !isDuplicate;
						})
						.slice(0, 5 - filteredUserDocuments.length);
					items.push(...filteredUserDocuments);
					items.push(...filteredRecentActivities);
				}
			} else {
				if (!serveUserShardUI && content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.slice(0, 5);
					items.push(...filteredRecentActivities);
				}
			}
			setRecentActivityCount(filteredRecentActivities);
			items.push({ ...searchResultsHeadingItem, includeInAnalytics: false });
			const numberOfSkeletons =
				primaryProduct === PrimaryProductKeys.Jira && fg('search_qf_increase_issues_in_jira')
					? 10
					: 5;
			for (let i = 0; i < numberOfSkeletons; i++) {
				items.push({ id: `skeleton-${i}`, type: 'skeleton' });
			}
		}

		if (state === State.POSTQUERY_NO_RESULTS) {
			items.push({ id: 'no-results', type: 'no-results' });
		}

		if (state === State.POSTQUERY_RESULTS) {
			if (
				'autocompleteSuggestions' in content &&
				Array.isArray(content.autocompleteSuggestions) &&
				fg('fe_quickfind_autocomplete_suggestions')
			) {
				const autocompleteSuggestions = content.autocompleteSuggestions;
				if (autocompleteSuggestions.length > 0) {
					const autocompleteSuggestionsHeadingItem = {
						id: 'autocomplete-suggestions',
						type: 'heading' as const,
						data: 'Autocomplete suggestions',
						includeInAnalytics: false,
					};
					items.push(autocompleteSuggestionsHeadingItem);

					autocompleteSuggestions.slice(0, 3).forEach((suggestion, index: number) => {
						items.push({
							id: `autocomplete-suggestion-${index}`,
							type: 'autocomplete-suggestion',
							data: suggestion.data,
						});
					});
				}
			}

			if (primaryProduct === PrimaryProductKeys.Jira) {
				items.push(
					...getJiraSuggestionsAndRecentQueries({
						recentQueries: content.recentQueries,
						zeroQueries: content.zeroQueries,
					}),
				);
			}

			filteredRecentActivities = content.recentActivities
				.filter((activity) => activity.data.node.object.data !== null)
				.slice(0, 5);

			const filteredUserDocuments = content.userDocuments.slice(0, 5);

			if (shouldUseBrowserHistory) {
				items.push(browserHistoryHeadingItem);
				const browserHistoryItemsPost = browserHistoryActivities.slice(0, 5).map((item) => ({
					id: item.id,
					type: 'browser-history',
					data: item.data,
				}));
				items.push(...browserHistoryItemsPost);
			} else if (userRelevantDocumentsEnabled) {
				if (content.userDocuments.length + content.recentActivities.length > 0) {
					items.push(recentsHeadingItem);
					filteredRecentActivities = content.recentActivities
						.filter((activity) => activity.data.node.object.data !== null)
						.filter((recentlyViewedResult) => {
							const isDuplicate = filteredUserDocuments.some((userDocument) => {
								const activityObject = recentlyViewedResult.data.node.object;
								const id = isGraphActivity(activityObject)
									? activityObject.data?.id
									: recentlyViewedResult.id;
								return id === userDocument.id;
							});
							return !isDuplicate;
						})
						.slice(0, 5 - filteredUserDocuments.length);
					items.push(...filteredUserDocuments);
					items.push(...filteredRecentActivities);
				}
			} else if (!serveUserShardUI) {
				if (filteredRecentActivities.length > 0) {
					items.push(recentsHeadingItem);
					items.push(...filteredRecentActivities);
				}
			}

			if (!serveUserShardUI) {
				setRecentActivityCount(filteredRecentActivities);
			}

			let uniqueMatchedRecentActivities: QuickFindResult<ActivityItemEdge>[] = [];
			let dedupedSearchResults: QuickFindResult<any>[] = [];
			const seenIds = new Set<string>();

			if (serveUserShardUI) {
				uniqueMatchedRecentActivities = filteredRecentActivities
					.filter((activity) => {
						const activityId = isGraphActivity(activity.data.node.object)
							? activity.data.node.object.data?.id
							: activity.id;
						const isInSearchResults = content.searchResults.some(
							(searchResult) => searchResult.id === activityId,
						);
						return !isInSearchResults;
					})
					.slice(0, 3);
				dedupedSearchResults = content.searchResults.filter((searchResult) => {
					const isDuplicatedResult = seenIds.has(searchResult.id);
					if (!isDuplicatedResult) {
						seenIds.add(searchResult.id);
					}
					return searchResult.id && !isDuplicatedResult;
				});
			} else {
				dedupedSearchResults = content.searchResults.filter((searchResult) => {
					const isInRecentActivities = filteredRecentActivities.some((recentlyViewedResult) => {
						const activityObject = recentlyViewedResult.data.node.object;
						const id = isGraphActivity(activityObject)
							? activityObject.data?.id
							: recentlyViewedResult.id;
						return id === searchResult.id;
					});

					const isInUserDocuments = content.userDocuments.some(
						(userDocument) => userDocument.id === searchResult.id,
					);

					const isDuplicateResult = seenIds.has(searchResult.id);
					if (!isDuplicateResult) {
						seenIds.add(searchResult.id);
					}

					return (
						searchResult.id && !isInRecentActivities && !isDuplicateResult && !isInUserDocuments
					);
				});
			}

			onSearchResultsShown(dedupedSearchResults);

			// The way product entities are shown depend on the primary product
			if (primaryProduct === PrimaryProductKeys.Confluence) {
				const confluenceSpaceResult = dedupedSearchResults.find((result) =>
					isSearchConfluenceSpace(result.data),
				);

				const showSearchResultsSection = Boolean(
					dedupedSearchResults.length +
					content.peopleAndTeams.length +
					uniqueMatchedRecentActivities.length,
				);

				if (showSearchResultsSection) {
					items.push(searchResultsHeadingItem);
					const filteredSearchResults = dedupedSearchResults.filter(
						(result) => !isSearchConfluenceSpace(result.data),
					);
					if (serveUserShardUI) {
						const fusionResults: { item: Item; timestamp: number }[] = [];
						fusionResults.push(
							...uniqueMatchedRecentActivities.map((activity) => ({
								item: activity,
								timestamp: Date.parse(activity.data.node.event.timestamp),
							})),
						);
						const maxSearchResultCount =
							(confluenceSpaceResult ? 10 : 11) - uniqueMatchedRecentActivities.length;
						fusionResults.push(
							...filteredSearchResults.slice(0, maxSearchResultCount).map((result) => ({
								item: result,
								timestamp: result.data.latestUserActionTs
									? Date.parse(result.data.latestUserActionTs)
									: 0,
							})),
						);
						fusionResults.sort((a, b) => b.timestamp - a.timestamp);
						items.push(...fusionResults.map((itemWithTimestamp) => itemWithTimestamp.item));
					} else {
						items.push(...filteredSearchResults.slice(0, confluenceSpaceResult ? 5 : 6));
					}
					confluenceSpaceResult && items.push(confluenceSpaceResult);
					items.push(...content.peopleAndTeams.slice(0, 2));
					items.push(...content.recentQueries.slice(0, 1));
				}
			} else if (primaryProduct === PrimaryProductKeys.Jira) {
				const showSearchResultsSection = Boolean(dedupedSearchResults.length);

				if (showSearchResultsSection) {
					const issueResults = dedupedSearchResults.filter((result) =>
						isSearchResultJiraIssue(result.data),
					);

					const nonIssueResults = dedupedSearchResults.filter(
						(result) => !isSearchResultJiraIssue(result.data),
					);

					const numberOfJiraIssues = fg('search_qf_increase_issues_in_jira') ? 10 : 5;
					items.push(searchResultsHeadingItem);
					items.push(...issueResults.slice(0, numberOfJiraIssues));
					items.push(...nonIssueResults.slice(0, 3));
				}
			} else {
				const showSearchResultsSection = Boolean(
					dedupedSearchResults.length + content.peopleAndTeams.length,
				);

				if (showSearchResultsSection) {
					items.push(searchResultsHeadingItem);
					items.push(...dedupedSearchResults.slice(0, 6));
					items.push(...content.peopleAndTeams.slice(0, 2));
					items.push(...content.recentQueries.slice(0, 1));
				}
			}
		}

		/* We don't want to interrupt the handleRecentActivitiesCallback with POSTQUERY_RESULTS
		 * as it's already handled in POSTQUERY_LOADING'
		 */
		if (state !== State.POSTQUERY_RESULTS) {
			handleRecentActivitiesCallback(state, filteredRecentActivities);
		}

		return items;
	}, [
		content,
		intl,
		userRelevantDocumentsEnabled,
		serveUserShardUI,
		serveZeroQuery,
		primaryProduct,
		onSearchResultsShown,
		handleRecentActivitiesCallback,
		serveFilterSuggestion,
	]);
};

const useAnalyticsEvents = (
	fireAnalyticsEvent: (payload: GasPayload) => void,
	commonAttributes: Record<string, any>,
	nonPrivacySafeAttributes: Record<string, any>,
) => {
	const { abortTask, isTaskActive } = useQuickFindPostQueryVitalStats();

	useEffect(() => {
		fireAnalyticsEvent(
			onSearchDialogShown({ attributes: commonAttributes, nonPrivacySafeAttributes }),
		);

		return () => {
			fireAnalyticsEvent(
				onSearchDialogDismissed({ attributes: commonAttributes, nonPrivacySafeAttributes }),
			);

			// Fire VitalStats taskAbort if a task is active when dialog is dismissed
			// Note: abortTask handles deferring the analytics event internally
			if (isTaskActive()) {
				abortTask('dialog_closed_during_network');
			}
		};
		// Only want these events to fire when the dialog opens and closes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

const useResetSessionAnalytics = () => {
	const { refreshSearchSessionId } = useBootstrapActions();
	const { resetQueryVersion } = useSearchActions();

	useEffect(() => {
		refreshSearchSessionId();
		resetQueryVersion();
	}, [refreshSearchSessionId, resetQueryVersion]);
};

export const showRovoTipBanner = (state: State, isAIEnabled: boolean = false) => {
	const isCorrectState = isPreQueryState(state) || isPostQueryLoadingOrResults(state);

	if (fg('rovo_search_updated_entitlement_checks_for_team_25')) {
		return isCorrectState && isAIEnabled;
	}

	return isCorrectState;
};
